import { useEffect, useState } from 'react';

import { Button, showSnackbar } from 'fluxo-ui';
import { AwardIcon, CopyIcon, EyeIcon } from 'fluxo-ui/icons';

import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import certificateService, { Certificate } from '../services/certificate.service';
import { formatDateTime } from '../utils/formatting';

const CertificatesPage: React.FC = () => {
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                const response = await certificateService.getMyCertificates({ limit: 100 });
                setCertificates(response.certificates || []);
            } catch (error: any) {
                showSnackbar(error.message || 'Failed to load certificates', 'Error', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchCertificates();
    }, []);

    const copyVerificationLink = (certificate: Certificate) => {
        const link = `${window.location.origin}/verify/${certificate.verificationCode}`;
        navigator.clipboard.writeText(link);
        showSnackbar('Verification link copied to clipboard', 'Copied', { type: 'success' });
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="My Certificates"
                description="Certificates you have earned. Share the verification link so anyone can confirm their authenticity."
                className="mb-0"
            />

            {loading ? (
                <Card>
                    <div className="text-center py-10 text-slate-500 dark:text-slate-400">Loading certificates...</div>
                </Card>
            ) : certificates.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={AwardIcon}
                        title="No certificates yet"
                        description="Pass a certification exam to earn your first certificate."
                    />
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {certificates.map((certificate) => (
                        <div
                            key={certificate._id}
                            className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                        >
                            <div className="h-2 bg-gradient-to-r from-teal-600 to-cyan-600" />
                            <div className="p-5 space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                                            {certificate.certificateData?.examTitle || 'Certificate'}
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            Issued {formatDateTime(certificate.issuedAt)}
                                        </p>
                                    </div>
                                    <Badge tone="success" className="shrink-0">
                                        {Math.round((certificate.certificateData?.score ?? 0) * 10) / 10}%
                                    </Badge>
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    ID: <code className="font-mono">{certificate.certificateId}</code>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        label="View"
                                        size="sm"
                                        variant="primary"
                                        leftIcon={EyeIcon}
                                        className="flex-1"
                                        onClick={() => certificateService.openCertificate(certificate)}
                                    />
                                    <Button
                                        label="Copy Link"
                                        size="sm"
                                        variant="default"
                                        layout="outlined"
                                        leftIcon={CopyIcon}
                                        className="flex-1"
                                        onClick={() => copyVerificationLink(certificate)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CertificatesPage;
